<script lang="ts">
	import type { FeedItemData } from '$lib/types/Feed';
	import type { EventGalleryData } from '$lib/queries/EventGalleryQuery';
	import { Breakpoints, Theme } from '@tools';
	import { personalizationTracking } from '@tools/PersonalizationTracking';
	import EventPoster from '@comp/EventPoster.svelte';
	import { ThinArrow } from '@icons';
	import { PersonalizationTypes } from '$lib/types/Personalization';
	import { contentSlide } from '$lib/animations/ContentTransitions';

	let { data }: { data: EventGalleryData } = $props();
	const events: (FeedItemData | undefined)[] = $derived(data.events);

	let numOfPosters = $derived.by(() => {
		if (Breakpoints.xl) {
			return 4;
		} else if (Breakpoints.lg) {
			return 3;
		} else if (Breakpoints.md) {
			return 2;
		} else {
			return 5;
		}
	});

	const trackClick = () => {
		if (data.galleryType === 'genre' && data.genre) {
			personalizationTracking.track(PersonalizationTypes.GENRE, [data.genre.id]);
			personalizationTracking.track(PersonalizationTypes.GALLERY, {
				type: data.galleryType,
				data: data.genre.id
			});
		} else if (data.galleryType === 'campaign') {
			personalizationTracking.track(PersonalizationTypes.GALLERY, { type: data.galleryType });
		}
	};

	const theme = new Theme(data.colorTheme);
</script>

{#if data.events.length >= 4}
	<section
		class="block:event-gallery block:large"
		style={theme.css}
		in:contentSlide|global
		out:contentSlide|global
	>
		<h3 class="text:h3">{data.title}</h3>
		<div class="events">
			{#each events as event, i}
				{#if i < numOfPosters}
					<EventPoster data={event} onclick={trackClick} />
				{/if}
			{/each}
			<div class="button">
				<a href={data.link} onclick={trackClick}>
					<p class="text:lg">{data.buttonText}</p>
					<span class="button:round"><ThinArrow /></span>
				</a>
			</div>
		</div>
	</section>
{/if}

<style lang="scss">
	section {
		position: relative;
		display: flex;
		flex-direction: column;
		align-items: stretch;
		margin-bottom: var(--spacing-auto);

		@include md {
			padding: 0 var(--gutter);
		}
	}

	h3 {
		text-transform: uppercase;
		padding: 0 var(--gutter);

		@include md {
			padding: 0;
		}
	}

	.events {
		grid-area: events;
		min-width: 100%;
		display: flex;
		flex-direction: row;
		align-items: stretch;
		justify-content: space-between;
		flex-wrap: nowrap;
		overflow-y: hidden;
		overflow-x: scroll;
		scroll-snap-type: x mandatory;
		scroll-behavior: smooth;
		scrollbar-width: none;
		padding-right: var(--gap);
		padding-top: $gap5;

		&::-webkit-scrollbar {
			display: none;
		}

		:global(.EventPoster) {
			padding-left: var(--gap);

			&:nth-child(1) {
				padding-left: var(--gutter);
			}
		}

		:global(.EventPoster .EventLabels) {
			margin-left: var(--gap);
		}

		:global(.EventPoster:first-child) {
			padding-left: var(--gutter);
		}

		@include md {
			overflow-x: hidden;
			padding-right: 0;
			gap: var(--gap);
			padding-top: var(--gap);

			:global(.EventPoster) {
				padding: 0;
			}

			:global(.EventPoster:first-child) {
				padding-left: 0;
			}

			:global(.EventPoster .EventLabels) {
				margin-left: unset;
			}
		}
	}

	.button {
		flex: 1 0 0;
		min-width: 46vw;
		display: flex;
		flex-direction: column;
		align-items: stretch;
		scroll-snap-align: center;
		padding-left: var(--gap);

		@include md {
			flex: 1 1 46vw;
			min-width: unset;
			padding: 0;
		}
	}

	a {
		aspect-ratio: 1 / #{$ratio1};
		background-color: var(--theme-primary);
		color: var(--theme-foreground);
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		padding: $gap3;
		cursor: pointer;
		text-decoration: none;

		@include md {
			padding: $gap4;
			padding-top: $gap6;

			p {
				transform: translateY(0em);
				transition: transform $transition;
			}

			&:hover p {
				transform: translateY(-#{$gap2});
			}
		}
	}

	span {
		margin-left: auto;
	}
</style>
